import React, { useState, useEffect } from 'react';
import { FaTicketAlt, FaCopy, FaCheck, FaShoppingCart } from 'react-icons/fa';

const Coupon = ({
  // Required
  labId,
  userId,
  
  // Test data for price/discount check
  testPrice = 0,
  sellingPrice = 0,
  discount = 0,
  
  // Display options
  showCopyButton = true,
  showDiscountValue = true,
  showCode = true,
  showLabel = true,
  labelText = "Coupon Code",
  showAddToCart = false,
  isInCart = false,
  onAddToCart = null,
  onRemoveFromCart = null,
  
  // Styling
  className = "",
  codeClassName = "",
  
  // Text
  loadingText = "Loading coupon...",
  noCouponText = "No coupon available",
}) => {
  const [coupon, setCoupon] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  // 🔥 Get userId from localStorage if not provided as prop
  const getUserId = () => {
    if (userId) return userId;
    
    try {
      const userData = JSON.parse(localStorage.getItem("medarphaUser") || "{}");
      return userData?.userId || null;
    } catch {
      return null;
    }
  };

  const currentUserId = getUserId();

  // 🔥 Check if discount is 0 or no price available
  const isPriceAvailable = sellingPrice > 0 || testPrice > 0;
  const hasDiscount = discount > 0;
  
  // 🔥 Show coupon when: NO discount (discount = 0) OR no price available
  const shouldShowCoupon = !hasDiscount || !isPriceAvailable;

  // Fetch coupon only if should show and userId exists
  useEffect(() => {
    if (!labId || !currentUserId || !shouldShowCoupon) {
      setCoupon(null);
      setLoading(false);
      return;
    }

    const fetchCoupon = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const url = `https://admintest.medrphalabs.com/api/Coupan/GetAllCoupon?userId=${currentUserId}&labId=${labId}`;
        
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error('Failed to fetch coupon');
        }
        
        const data = await response.json();
        const coupons = data.MainCoupons || data.UserCoupons || [];
        setCoupon(coupons.length > 0 ? coupons[0] : null);
        
      } catch (err) {
        console.error('Coupon fetch error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCoupon();
  }, [labId, currentUserId, shouldShowCoupon]);

  // Handle copy
  const handleCopy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  };

  // 🔥 If discount exists ( > 0 ) AND price is available, return null (hide coupon)
  if (hasDiscount && isPriceAvailable) {
    return null;
  }

  // 🔥 If no userId, show login message
  if (!currentUserId) {
    return (
      <div className={`text-center text-[10px] text-gray-400 ${className}`}>
        Login to view coupons
      </div>
    );
  }

  // Loading
  if (loading) {
    return (
      <div className={`flex justify-center items-center gap-2 text-gray-500 ${className}`}>
        <div className="w-3.5 h-3.5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        <span className="text-[10px]">{loadingText}</span>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className={`text-center text-[10px] text-red-500 ${className}`}>
        {error}
      </div>
    );
  }

  // No coupon
  if (!coupon) {
    return (
      <div className={`text-center text-[10px] text-gray-400 ${className}`}>
        {noCouponText}
      </div>
    );
  }

  const code = coupon.Code || coupon.couponCode || '';
  const discountValue = coupon.DiscountValue || coupon.discount || 0;
  const discountType = coupon.DiscountType || coupon.type || 'percentage';
  const description = coupon.Description || coupon.description || '';

  const discountDisplay = discountValue > 0 
    ? discountType === 'percentage' 
      ? `${discountValue}% OFF` 
      : `₹${discountValue} OFF`
    : '';

  return (
    <div className={`w-full ${className}`}>
      {/* 🔥 Add to Cart Button - Top Right */}
      {showAddToCart && onAddToCart && (
        <div className="flex justify-end mb-1.5">
          <button
            onClick={isInCart ? onRemoveFromCart : onAddToCart}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[10px] font-semibold border transition-all duration-200
              ${isInCart
                ? "bg-red-50 text-red-600 border-red-500 hover:bg-red-100"
                : "bg-blue-50 text-blue-600 border-blue-500 hover:bg-blue-100"
              }`}
          >
            <FaShoppingCart size={10} />
            {isInCart ? "Remove" : "Add"}
          </button>
        </div>
      )}

      {/* 🔥 Coupon - Dashed Red Border */}
      <div className="w-full border-2 border-dashed border-red-400 rounded-lg p-2.5 bg-gradient-to-r from-red-50 to-red-100/30 shadow-sm hover:shadow-md transition-shadow duration-300">
        <div className="flex items-center justify-center gap-2 flex-wrap">
          
          {/* Coupon Icon */}
          <div className="flex-shrink-0">
            <FaTicketAlt className="text-red-500" size={14} />
          </div>

          {/* Coupon Code - Highlighted */}
          {showCode && code && (
            <span className={`font-mono font-bold text-red-600 text-sm tracking-wider ${codeClassName}`}>
              {code}
            </span>
          )}
          
          {/* Discount Badge */}
          {showDiscountValue && discountDisplay && (
            <span className="text-[10px] font-bold text-white bg-gradient-to-r from-red-500 to-red-600 px-2.5 py-0.5 rounded-full shadow-sm">
              {discountDisplay}
            </span>
          )}
          
          {/* Description */}
          {description && (
            <span className="text-[9px] text-gray-500 truncate max-w-[120px]">
              {description}
            </span>
          )}

          {/* Copy Button */}
          {showCopyButton && code && (
            <button
              onClick={() => handleCopy(code)}
              className={`flex items-center gap-1 px-3 py-1 rounded-md text-[9px] font-medium transition-all duration-200
                ${copied 
                  ? "bg-green-500 text-white" 
                  : "bg-red-500 text-white hover:bg-red-600"
                }`}
            >
              {copied ? (
                <>
                  <FaCheck size={9} />
                  <span>Copied!</span>
                </>
              ) : (
                <>
                  <FaCopy size={9} />
                  <span>Copy</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Coupon;